import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Slot, SlotFilters, SlotListResponse } from '../types';

interface SlotSearchContextType {
  filters: SlotFilters;
  updateFilters: (filters: Partial<SlotFilters>) => void;
  clearFilters: () => void;
  results: Slot[];
  totalResults: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setResults: (response: SlotListResponse) => void;
  selectedSlots: number[];
  toggleSlotSelection: (slotId: number) => void;
  clearSelection: () => void;
  selectedFairId: number | null;
  setSelectedFairId: (fairId: number | null) => void;
}

const defaultFilters: SlotFilters = {
  slot_type: [],
  has_power: undefined,
  min_price: undefined,
  max_price: undefined,
  is_available: true,
  search: '',
};

const SlotSearchContext = createContext<SlotSearchContextType | null>(null);

export function SlotSearchProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<SlotFilters>(defaultFilters);
  const [results, setResultsState] = useState<Slot[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<number | null>(null);

  const updateFilters = useCallback((newFilters: Partial<SlotFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to page 1 when filters change
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setCurrentPage(1);
  }, []);

  const setResults = useCallback((response: SlotListResponse) => {
    setResultsState(response.items);
    setTotalResults(response.total);
    setTotalPages(response.total_pages);
  }, []);

  const toggleSlotSelection = useCallback((slotId: number) => {
    setSelectedSlots(prev => {
      if (prev.includes(slotId)) {
        return prev.filter(id => id !== slotId);
      }
      // Maximum 3 slots for comparison
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, slotId];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSlots([]);
  }, []);

  return (
    <SlotSearchContext.Provider
      value={{
        filters,
        updateFilters,
        clearFilters,
        results,
        totalResults,
        currentPage,
        totalPages,
        pageSize,
        setCurrentPage,
        setPageSize,
        isLoading,
        setIsLoading,
        setResults,
        selectedSlots,
        toggleSlotSelection,
        clearSelection,
        selectedFairId,
        setSelectedFairId,
      }}
    >
      {children}
    </SlotSearchContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSlotSearch() {
  const context = useContext(SlotSearchContext);
  if (!context) {
    throw new Error('useSlotSearch must be used within a SlotSearchProvider');
  }
  return context;
}

export default SlotSearchContext;
